import { CommonModule } from '@angular/common'
import { Component, ElementRef, HostListener, inject, Input } from '@angular/core'
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms'

/**
 * Registration-form social login picker, shown beside the language chips.
 * LinkedIn is the only provider for now.
 */
@Component({
  selector: 'ngx-form-social-login',
  templateUrl: './form-social-login.component.html',
  standalone: true,
  imports: [CommonModule]
})
export class FormSocialLoginComponent {
  /** Persona form that owns options.social_login */
  @Input()
  form: UntypedFormGroup

  /** Whether the provider menu is open */
  menuOpen = false

  /** LinkedIn checkbox state inside the open menu, applied only on Done */
  draftLinkedin = false

  private readonly _host = inject(ElementRef<HTMLElement>)

  /**
   * Whether LinkedIn is enabled on this registration form.
   * @returns boolean
   */
  get linkedinEnabled(): boolean {
    return this.form?.get('options.social_login.linkedin')?.value === true
  }

  /**
   * Opens or closes the social login menu. Opening copies the saved selection into the draft.
   * @returns void
   */
  toggleMenu(): void {
    if (!this.menuOpen) this.draftLinkedin = this.linkedinEnabled
    this.menuOpen = !this.menuOpen
  }

  /**
   * Updates the in-menu LinkedIn checkbox without changing the chips.
   * @param enabled boolean
   * @returns void
   */
  setDraftLinkedIn(enabled: boolean): void {
    this.draftLinkedin = enabled === true
  }

  /**
   * Writes the draft selection onto the persona form and closes the menu.
   * @returns void
   */
  applySelection(): void {
    this.setLinkedIn(this.draftLinkedin)
    this.menuOpen = false
  }

  /**
   * Closes the menu when the click lands outside this control.
   * @param event MouseEvent
   * @returns void
   */
  @HostListener('document:click', ['$event'])
  closeMenuOnOutsideClick(event: MouseEvent): void {
    if (!this.menuOpen) return
    const target = event.target
    if (target instanceof Node && this._host.nativeElement.contains(target)) return
    this.menuOpen = false
  }

  /**
   * Stores LinkedIn social login on the persona options. True adds linkedin: true.
   * @param enabled boolean
   * @returns void
   */
  setLinkedIn(enabled: boolean): void {
    const options = this.form?.get('options') as UntypedFormGroup
    if (!options) return

    let socialLogin = options.get('social_login') as UntypedFormGroup
    if (!socialLogin || !(socialLogin.get('linkedin') instanceof UntypedFormControl)) {
      socialLogin = new UntypedFormGroup({
        linkedin: new UntypedFormControl(false)
      })
      options.setControl('social_login', socialLogin)
    }

    socialLogin.get('linkedin').setValue(enabled === true)
    socialLogin.get('linkedin').markAsDirty()
    options.markAsDirty()
  }
}
